import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
// import "../../css/EnterpriseDetails.css";
import "../../css/DocumentHistoryResponsive.css";
import { getFilteredDocs } from "../../utils/approvedContentTabs";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import { formatFileType } from "../../utils/FileType";

// Icons
import viewIcon from "../../assets/UserIcons/ViewIcon.svg";
import folderIcon from "../../assets/Images/folder.png";
import downloadIcon from "../../assets/Images/Download.png";
import filterIcon from "../../assets/Images/Filter.png";
import changeStatusIcon from "../../assets/icons/changeStatusIcon.png";
import deleteIcon from "../../assets/UserIcons/DeleteIcon.svg";
import trainDoc from "../../assets/UserIcons/FileIcon.svg";
import trainText from "../../assets/icons/TrainText.png";
import trainUrls from "../../assets/UserIcons/Website.svg";
import trainVideo from "../../assets/UserIcons/VideoIcon.svg";
import trainQA from "../../assets/icons/TrainQA.png";
import searchIcon from "../../assets/UserIcons/Search.svg";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";

const DocumentHistory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [allApprovedDocs, setAllApprovedDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname;

  const tabs = [
    { name: "Documents", path: "/superadmin/knowledge-repository" },
    { name: "Web URLs", path: "/superadmin/knowledgerepository-weburls" },
    { name: "Video URLs", path: "/superadmin/knowledgerepository-videourls" },
    // { name: "Deleted", path: "/superadmin/knowledgerepository-deletedocs" },
    // { name: "Text", path: "/superadmin/texthistory" },
    // { name: "Q&A", path: "/superadmin/qahistory" },
  ];

  const [stats, setStats] = useState({
    user_files_active_count: 0,
    user_files_inactive_count: 0,
    uploaded_files_active_count: 0,
    uploaded_files_inactive_count: 0,
  });

  // ✅ Fetch stats
  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("access_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/files/all-stats`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setStats(data.data || {});
      } else {
        // showErrorToast(data.message || "Failed to load document stats");
      }
    } catch (error) {
      console.error("❌ Error fetching stats:", error);
      showErrorToast("Something went wrong while fetching stats");
    } finally {
      setIsLoading(false);
    }
  };

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchGlobalUserDocs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/global-all-data`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAllApprovedDocs(res.data);
      // console.log(res.data);
    } catch (error) {
      console.error("❌ Error fetching approved docs:", error);
      // showErrorToast(TOAST_MESSAGES.DOCUMENT_HISTORY.FETCH_FAILED);
    }
  };

  useEffect(() => {
    fetchGlobalUserDocs();
    fetchStats();
  }, []);

  const handleDownload = async (pythonFileUUID) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/download`,
        {
          params: { python_file_uuid: pythonFileUUID },
          responseType: "blob", // required for file download
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const blob = response.data;

      // ✅ Get filename from Content-Disposition header
      let filename = "downloaded_file";
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i,
        );
        if (match && match[1]) filename = decodeURIComponent(match[1]);
      }

      // ✅ Trigger download (unchanged)
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showSuccessToast("File downloaded successfully!");
    } catch (error) {
      console.error("❌ Download failed:", error);
      showErrorToast("Unable to download the file. Please try again.");
    }
  };

  // Delete function
  const handleDeleteDocument = async (fileId) => {
    // if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await api.patch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/docs/soft-delete/${fileId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      showSuccessToast("Document deleted successfully");
      // Refresh data after delete
      fetchGlobalUserDocs();
    } catch (error) {
      console.error("❌ Error deleting document:", error);
      showErrorToast("Failed to delete document");
    }
  };

  const documentFiles = getFilteredDocs(allApprovedDocs, "document");

  const filteredFiles = documentFiles
    .filter((file) =>
      file.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((file) => {
      if (!dayFilter) return true;
      const updatedDate = new Date(file.updatedAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return updatedDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return updatedDate >= yesterday && updatedDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredFiles.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredFiles.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const statsData = [
    {
      label: "Uploaded Documents",
      value: getFilteredDocs(allApprovedDocs, "document").length,
      icon: trainDoc,
      iconClass: "train-doc-icon",
    },
    {
      label: "Uploaded Web URLs",
      value: getFilteredDocs(allApprovedDocs, "webpage").length,
      icon: trainUrls,
    },
    // {
    //   label: "Uploaded Text",
    //   value: allApprovedDocs.filter((doc) => doc.type === "text").length,
    //   icon: trainText,
    // },
    {
      label: "Uploaded Video URLs",
      value: allApprovedDocs.filter((doc) => doc.type === "youtube_audio")
        .length,
      icon: trainVideo,
    },
    // {
    //   label: "Uploaded Q&A",
    //   value: allApprovedDocs.filter((doc) => doc.type === "qa").length,
    //   icon: trainQA,
    // },
    // {
    //   label: "Deleted Documents",
    //   value: stats.uploaded_files_inactive_count || 0,
    //   icon: deleteIcon,
    // },
  ];

  // View (Open) File using Google Docs Viewer (opens Excel/Word/PPT online)
  // const handleViewFile = (python_file_uuid) => {
  //   const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/view?python_file_uuid=${python_file_uuid}`;

  //   const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
  //     fileUrl
  //   )}&embedded=true`;

  //   window.open(googleViewUrl, "_blank");
  // };

  const handleViewFile = (pythonFileUuid, originalFileName = "") => {
    const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/view?python_file_uuid=${pythonFileUuid}`;

    // console.log("file url for the file", fileUrl);

    // ✅ Detect image by extension
    const imageExtensions = ["png", "jpg", "jpeg", "webp", "gif"];
    const extension = originalFileName.split(".").pop()?.toLowerCase();

    // console.log("Detected extension:", extension);

    if (imageExtensions.includes(extension)) {
      // console.log("🖼 Opening image directly");
      window.open(fileUrl, "_blank");
      return;
    }

    // console.log("📄 Opening via Google Docs Viewer");
    const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
      fileUrl,
    )}&embedded=true`;

    window.open(googleViewUrl, "_blank");
  };

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const handleChangeFileStatus = async (python_file_uuid) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/update_file_status/${python_file_uuid}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // ❌ No body sent → Backend toggles automatically
        },
      );

      const result = await response.json();

      if (!response.ok) {
        showErrorToast(result.message || "Failed to update file status");
        return;
      }

      showSuccessToast("Status updated successfully");

      // refresh file list
      fetchGlobalUserDocs();
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast("Something went wrong");
    }
  };

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-2">
              {item.icon && (
                <img
                  // className="icon-image"
                  className={`icon-image ${item.iconClass || ""}`}
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                />
              )}
              <h6 className="ms-2 mb-0 ak-text-md ">{item.label}</h6>
            </div>

            <h2 className="fw-bold ak-text-md">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold ak-text-lg">Uploaded Documents</span>
      </div>

      {/* <div className="mb-3 mt-3 tabs-wrapper">
        <div className="d-flex p-2 rounded border tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-button ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div> */}
      <div className="mt-3 tabs-wrapper">
        <div className="tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              style={{
                fontWeight: currentTab === tab.path ? "700" : "500", // inline desktop logic
              }}
              className={`tab-button ak-text-md ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>
      {documentFiles?.length === 0 ? (
        <></>
      ) : (
        <div
          style={{ borderLeft: "1px solid var(--dark-color) " }}
          className="d-flex px-2 py-1 justify-content-between align-items-center "
        >
          <div className="d-flex align-items-center">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={handleEntriesChange}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="ms-2 mt-2 ak-text-md ">Entries per page</label>
          </div>

          {/* Search input */}
          <div className="search d-flex align-items-center gap-2">
            <div
              className="input-group custom-search-group"
              style={{ width: "320px" }}
            >
              <span className="input-group-text">
                {/* <i className="bi bi-search"></i> */}
                <img className="icon-image" src={searchIcon} alt="Search" />
              </span>
              <input
                type="text"
                className="form-control ak-text-md "
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="">
              <button
                className="icon-button filter-dropdown-toggle action-button"
                type="button"
                data-bs-toggle="dropdown"
                title="Filter"
              >
                <img src={filterIcon} className="icon-image" alt="Filter" />
              </button>

              <div
                className="dropdown-menu profile-menu-dropdown p-2"
                style={{ minWidth: "200px" }}
              >
                <h6 className="fw-bolder refresh-prompts">Time</h6>
                <hr />
                {["asc", "desc"].map((order) => (
                  <div className="form-check" key={order}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sortOrder"
                      id={order}
                      value={order}
                      checked={sortOrder === order}
                      onChange={() => setSortOrder(order)}
                    />
                    <label className="form-check-label" htmlFor={order}>
                      {order === "asc" ? "Ascending" : "Descending"}
                    </label>
                  </div>
                ))}

                <hr />
                <h6 className="fw-bolder refresh-promts">By Day</h6>
                <hr />
                {["Today", "Yesterday", ""].map((day) => (
                  <div className="form-check" key={day || "all"}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="dayFilter"
                      id={day || "all"}
                      value={day}
                      checked={dayFilter === day}
                      onChange={() => setDayFilter(day)}
                    />
                    <label className="form-check-label" htmlFor={day || "all"}>
                      {day || "All"}
                    </label>
                  </div>
                ))}

                <hr />
                <div className="d-flex justify-content-between mt-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    className="btn dropdown-btn bg-dark text-white btn-sm"
                    onClick={() => {
                      setCurrentPage(1);
                    }}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted  profile-i ak-text-md ">
            {/* There are no document(s) in this section. */}
            No documents have been trained yet.
          </p>
        </div>
      ) : (
        <div
          className="not-disable"
          style={{ borderLeft: "1px solid var(--dark-color) " }}
        >
          <div className="table-responsive ak-text-md ">
            <table className="table  table-sm w-100">
              <thead className="text-left">
                <tr>
                  <SortableHeader
                    column="name"
                    label="Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="file_extension"
                    label="Type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="size"
                    label="Size"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="approval_status"
                    label="Document Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="mode"
                    label="Mode"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* ✅ Enterprise Column */}
                  <SortableHeader
                    column="enterprise_name"
                    label="Enterprise"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* ✅ Uploaded By Column */}
                  <SortableHeader
                    column="uploaded_by"
                    label="Uploaded By"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* ✅ New Department Column */}
                  <SortableHeader
                    column="department_name"
                    label="Department"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* ✅ New Created By Column */}
                  <SortableHeader
                    column="created_by"
                    label="Created By"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="file_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="createdAt"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updatedAt"
                    label="Last Updated On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th className="text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((file, index) => (
                  <tr key={file.id || index}>
                    <td
                      data-label="Document Name"
                      className="text-truncate text-left"
                      style={{ maxWidth: "180px" }}
                      title={file.name}
                    >
                      {file.name.replace(/\.[^/.]+$/, "")}
                    </td>

                    <td data-label="Document Type" className="text-left">
                      {formatFileType(file.type, file.name)}
                    </td>

                    <td className="text-left" data-label="Document Size">
                      {formatFileSize(file.size)}
                    </td>

                    <td className="text-left" data-label="Document Status">
                      <span
                        className={`badge ${
                          file.approval_status === "approved"
                            ? "bg-success"
                            : file.approval_status === "pending"
                              ? "bg-warning"
                              : "bg-danger"
                        }`}
                      >
                        {file.approval_status.charAt(0).toUpperCase() +
                          file.approval_status.slice(1)}
                      </span>
                    </td>

                    <td className="text-left" data-label="Mode">
                      <span
                        className="px-2 py-1 text-sm rounded-md text-[var(--primary-color)]"
                        style={{ display: "inline-block" }}
                      >
                        {(file.mode || "Personal").charAt(0).toUpperCase() +
                          (file.mode || "Personal").slice(1)}
                      </span>
                    </td>

                    {/* ✅ Enterprise Column */}
                    <td
                      className="text-left text-truncate"
                      data-label="Enterprise"
                      style={{ maxWidth: "150px" }}
                      title={file.enterprise_name}
                    >
                      {file.enterprise_name || "N/A"}
                    </td>

                    {/* ✅ Uploaded By Column */}
                    <td
                      className="text-left text-truncate"
                      data-label="Uploaded By"
                      style={{ maxWidth: "150px" }}
                      title={file.uploaded_by}
                    >
                      {(file.uploaded_by || "User").charAt(0).toUpperCase() +
                        (file.uploaded_by || "User").slice(1)}
                    </td>

                    {/* ✅ Department Column */}
                    <td
                      className="text-left text-truncate"
                      data-label="Department"
                      style={{ maxWidth: "150px" }}
                      title={file.department_name}
                    >
                      {file.department_name || "N/A"}
                    </td>

                    {/* ✅ Created By Column */}
                    <td
                      className="text-left text-truncate"
                      data-label="Created By"
                      style={{ maxWidth: "150px" }}
                      title={file.created_by}
                    >
                      {(file.created_by || "User").charAt(0).toUpperCase() +
                        (file.created_by || "User").slice(1)}
                    </td>

                    <td className="text-left">
                      <span
                        className={`badge ${
                          file.file_status === "1" ? "bg-success" : "bg-warning"
                        }`}
                      >
                        {file.file_status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="text-left" data-label="Uploaded On">
                      {formatDateToUserTimeZone(file.createdAt)}
                    </td>

                    <td className="text-left" data-label="Last Updated On">
                      {formatDateToUserTimeZone(file.updatedAt)}
                    </td>

                    <td className="text-right">
                      <button
                        className="icon-button"
                        onClick={() => handleDownload(file.python_file_uuid)}
                        title="Download File"
                      >
                        <img
                          className="icon-image"
                          src={downloadIcon}
                          alt="Download File"
                        />
                      </button>

                      <button
                        onClick={() =>
                          file.mode?.toLowerCase() !== "global" &&
                          handleChangeFileStatus(file.python_file_uuid)
                        }
                        className={`icon-button ${
                          file.mode?.toLowerCase() === "global"
                            ? "disabled-icon"
                            : ""
                        }`}
                        title={
                          file.mode?.toLowerCase() === "global"
                            ? "Status change disabled for Global mode"
                            : "Change Status"
                        }
                        disabled={file.mode?.toLowerCase() === "global"}
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                          style={{
                            opacity:
                              file.mode?.toLowerCase() === "global" ? 0.4 : 1,
                            cursor:
                              file.mode?.toLowerCase() === "global"
                                ? "not-allowed"
                                : "pointer",
                          }}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>

        </div>
      )}
    </div>
  );
};

export default DocumentHistory;
